import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { Button } from "../components/atoms";
import { fetchStore } from "../api/stores";
import { fetchOrganizations } from "../api/organizations";
import { fetchDepartments } from "../api/departments";
import {
    DEFAULT_STORE_STATUS,
    STORE_CODE_MAX_LENGTH,
    STORE_NAME_MAX_LENGTH,
    STORE_SHORT_NAME_MAX_LENGTH,
    STORE_STATUS_OPTIONS,
    validateStoreCode,
    validateStoreName,
    validateStoreShortName,
    type StoreFormOutletContext,
    type StoreFormState,
} from "../components/StoreEditLayout";

/**
 * フォームフィールドラッパー
 */
const FormField = ({
    label,
    required = false,
    children,
}: {
    label: string;
    required?: boolean;
    children: React.ReactNode;
}) => (
    <div className="flex items-start gap-6">
        <label className="w-28 text-sm text-gray-700 pt-2 flex-shrink-0">
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <div className="flex-1">{children}</div>
    </div>
);

interface BasicFormData {
    code: string;
    name: string;
    shortName: string;
    status: number;
}

/**
 * 店舗登録・編集ページ（基本情報タブ）
 */
const StoreFormPage = () => {
    const navigate = useNavigate();
    const {
        setPageError,
        invalidFields,
        pendingFormState,
        setPendingFormState,
    } = useOutletContext<StoreFormOutletContext>();
    const { id } = useParams<{ id?: string }>();
    const isEdit = !!id;
    const storeId = id ? parseInt(id, 10) : null;

    // 編集モードで pendingFormState が無い場合（初回 or 店舗切替）のみ DB 取得
    const needsFetch = isEdit && storeId !== null && pendingFormState === null;
    const [loading, setLoading] = useState(needsFetch);

    const [formData, setFormData] = useState<BasicFormData>({
        code: pendingFormState?.code ?? "",
        name: pendingFormState?.name ?? "",
        shortName: pendingFormState?.shortName ?? "",
        status: pendingFormState?.status ?? DEFAULT_STORE_STATUS,
    });

    // 編集時にデータを取得（pendingFormState 未設定時だけ）
    useEffect(() => {
        if (!needsFetch || storeId === null) return;
        const loadData = async () => {
            setLoading(true);
            setPageError(null);
            try {
                const [store, orgsRes, deptsRes] = await Promise.all([
                    fetchStore(storeId),
                    fetchOrganizations(),
                    fetchDepartments(),
                ]);

                const orgsById = new Map(orgsRes.items.map((o) => [o.id, o]));

                // 所属組織: store.organization_id (level=11) の親方向に root まで辿って orgLevels を構築
                // OrganizationSelectPopup は organization.level をキー (0..9) とする
                // 店舗 (level=11) の親方向に辿って、各祖先を level でキー化する
                const orgLevels: Record<number, { id: number; name: string }> = {};
                if (store.organization_id !== null) {
                    const ownOrg = orgsById.get(store.organization_id);
                    let parentId = ownOrg?.parent_organization_id ?? null;
                    while (parentId !== null) {
                        const p = orgsById.get(parentId);
                        if (!p) break;
                        orgLevels[p.level] = { id: p.id, name: p.name ?? "" };
                        parentId = p.parent_organization_id;
                    }
                }

                // 使用部門: 店舗 org の子 (level=12) の name と一致する department を探して id 化
                const childDeptOrgs = orgsRes.items.filter(
                    (o) =>
                        o.parent_organization_id === store.organization_id &&
                        o.level === 12,
                );
                const deptByName = new Map(deptsRes.items.map((d) => [d.name, d]));
                const departmentIds = childDeptOrgs
                    .map((o) => (o.name ? deptByName.get(o.name)?.id : undefined))
                    .filter((id): id is number => id !== undefined);

                const next: StoreFormState = {
                    code: store.code ?? "",
                    name: store.name,
                    shortName: store.short_name ?? "",
                    status: store.status ?? DEFAULT_STORE_STATUS,
                    organization:
                        Object.keys(orgLevels).length > 0 ? { orgLevels } : null,
                    departmentIds,
                };
                setFormData({
                    code: next.code,
                    name: next.name,
                    shortName: next.shortName,
                    status: next.status,
                });
                setPendingFormState(next);
            } catch (err) {
                console.error("Failed to fetch store:", err);
                setPageError(err instanceof Error ? err.message : "店舗データの取得に失敗しました");
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [needsFetch, storeId, setPageError, setPendingFormState]);

    const syncPending = useCallback(
        (next: BasicFormData) => {
            setPendingFormState({
                code: next.code,
                name: next.name,
                shortName: next.shortName,
                status: next.status,
                organization: pendingFormState?.organization ?? null,
                departmentIds: pendingFormState?.departmentIds ?? [],
            });
        },
        [pendingFormState, setPendingFormState],
    );

    const handleInputChange = useCallback(
        <K extends keyof BasicFormData>(field: K, value: BasicFormData[K]) => {
            setFormData((prev) => {
                const updated = { ...prev, [field]: value };
                syncPending(updated);
                return updated;
            });
        },
        [syncPending],
    );

    const isFieldInvalid = (key: keyof StoreFormState): boolean => {
        if (!invalidFields.includes(key)) return false;
        const value = formData[key as "code" | "name" | "shortName"];
        if (typeof value !== "string") return false;
        return !value || !value.trim();
    };

    const handleCancel = () => {
        navigate("/org-settings/store");
    };

    // 書式エラー（ライブ検証）
    const codeError = validateStoreCode(formData.code);
    const nameError = validateStoreName(formData.name);
    const shortNameError = validateStoreShortName(formData.shortName);

    const handleNext = () => {
        const path = isEdit
            ? `/org-settings/store/${id}/organization`
            : "/org-settings/store/new/organization";
        navigate(path);
    };

    if (loading) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <span className="text-gray-500">読み込み中...</span>
            </div>
        );
    }

    return (
        <div className="flex-1 flex flex-col bg-white rounded-lg overflow-hidden">
            <div className="flex-1 overflow-auto p-6">
                <div className="space-y-6">
                    <div className="text-xs text-red-500">「*」は登録必須項目です。</div>

                    {/* 店舗コード */}
                    <FormField label="店舗コード" required>
                        <input
                            type="text"
                            value={formData.code}
                            onChange={(e) => handleInputChange("code", e.target.value)}
                            placeholder="001"
                            className={clsx(
                                "form-input-field w-60",
                                (isFieldInvalid("code") || codeError !== null) && "border-red-500",
                            )}
                        />
                        {codeError !== null && (
                            <p className="mt-1 text-xs text-red-500">
                                半角英数字{STORE_CODE_MAX_LENGTH}桁以内
                            </p>
                        )}
                    </FormField>

                    {/* 店舗名 */}
                    <FormField label="店舗名" required>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleInputChange("name", e.target.value)}
                            placeholder="渋谷店"
                            className={clsx(
                                "form-input-field w-60",
                                (isFieldInvalid("name") || nameError !== null) && "border-red-500",
                            )}
                        />
                        {nameError !== null && (
                            <p className="mt-1 text-xs text-red-500">
                                {STORE_NAME_MAX_LENGTH}文字以内
                            </p>
                        )}
                    </FormField>

                    {/* 略称 */}
                    <FormField label="略称">
                        <input
                            type="text"
                            value={formData.shortName}
                            onChange={(e) => handleInputChange("shortName", e.target.value)}
                            placeholder="渋谷"
                            className={clsx(
                                "form-input-field w-60",
                                shortNameError !== null && "border-red-500",
                            )}
                        />
                        {shortNameError !== null && (
                            <p className="mt-1 text-xs text-red-500">
                                {STORE_SHORT_NAME_MAX_LENGTH}文字以内
                            </p>
                        )}
                    </FormField>

                    {/* ステータス */}
                    <FormField label="ステータス">
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-2">
                            {STORE_STATUS_OPTIONS.map((opt) => (
                                <label
                                    key={opt.value}
                                    className="flex items-center gap-2 cursor-pointer"
                                >
                                    <input
                                        type="radio"
                                        name="store-status"
                                        checked={formData.status === opt.value}
                                        onChange={() => handleInputChange("status", opt.value)}
                                    />
                                    <span className="text-sm text-gray-700">{opt.label}</span>
                                </label>
                            ))}
                        </div>
                    </FormField>
                </div>
            </div>

            {/* フッターボタン */}
            <div className="flex justify-end gap-4 px-6 py-4">
                <Button variant="outline" size="md" onClick={handleCancel} className="px-8">
                    キャンセル
                </Button>
                <Button variant="primary" size="md" onClick={handleNext} className="px-8">
                    2.組織の設定へ進む
                </Button>
            </div>
        </div>
    );
};

export default StoreFormPage;
